/**
 * Messages between the theme builder (parent window) and the storefront preview (iframe), version 1.
 * Both sides validate every message with these parsers and check `event.origin` before using it.
 */
import type { ContentLanguage } from "./content";

export const PREVIEW_PROTOCOL_VERSION = 1 as const;

export type BuilderToPreview =
  | { v: 1; type: "config:replace"; settings: { config?: unknown; pages?: unknown; seo?: unknown } }
  | { v: 1; type: "page:change"; page: string }
  | { v: 1; type: "section:select"; id: string | null }
  /** Campaign builder: unsaved campaign fields laid over the saved campaign in the preview. */
  | { v: 1; type: "campaign:replace"; offer: CampaignDraft };

/** The campaign fields the builder may change live (everything else comes from the saved offer). */
export interface CampaignDraft {
  name?: string;
  offer_rules?: Record<string, unknown>;
  ui_content?: Record<string, unknown>;
}

export type PreviewToBuilder =
  | { v: 1; type: "ready" }
  | { v: 1; type: "section:clicked"; id: string }
  | { v: 1; type: "section:hover"; id: string | null }
  | { v: 1; type: "navigate"; path: string }
  /** The storefront header or footer, which are edited in the theme settings rather than as page sections. */
  | { v: 1; type: "region:clicked"; region: PreviewRegion }
  | { v: 1; type: "region:hover"; region: PreviewRegion | null }
  /** Text typed inline in the selected section: `path` is the field key (`slides.0.title` for array items). */
  | { v: 1; type: "field:edit"; sectionId: string; path: string; locale: ContentLanguage; value: string }
  /** An image clicked in the selected section: the editor opens its media picker for that field. */
  | { v: 1; type: "image:pick"; sectionId: string; path: string }
  /** Something in the selected section clicked whose settings are not text (a link, products, a date): the editor
   * opens a small dialog with just those fields. */
  | { v: 1; type: "fields:open"; sectionId: string; paths: string[]; label?: string }
  /** An editor shortcut pressed while the preview has focus (keys go to the iframe, not the editor). */
  | { v: 1; type: "shortcut"; action: PreviewShortcut }
  | { v: 1; type: "error"; message: string };

export type PreviewRegion = "header" | "footer";

export type PreviewShortcut = "undo" | "redo" | "save";

/** Longest inline text accepted from the preview. */
export const MAX_INLINE_TEXT = 2000;

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);
const isId = (x: unknown): x is string => typeof x === "string" && x.length > 0 && x.length <= 200;
/** A section field key: dotted names, with numeric segments for array items. */
const isFieldPath = (x: unknown): x is string =>
  typeof x === "string" && x.length <= 120 && /^[A-Za-z]\w*(\.(\d{1,3}|[A-Za-z]\w*))*$/.test(x);
const isRegion = (x: unknown): x is PreviewRegion => x === "header" || x === "footer";

export const parseBuilderMessage = (data: unknown): BuilderToPreview | null => {
  if (!isObj(data) || data.v !== PREVIEW_PROTOCOL_VERSION) return null;
  switch (data.type) {
    case "config:replace":
      return isObj(data.settings) ? { v: 1, type: "config:replace", settings: data.settings } : null;
    case "page:change":
      return isId(data.page) ? { v: 1, type: "page:change", page: data.page } : null;
    case "section:select":
      return data.id === null || isId(data.id) ? { v: 1, type: "section:select", id: data.id as string | null } : null;
    case "campaign:replace": {
      if (!isObj(data.offer)) return null;
      const o = data.offer;
      const offer: CampaignDraft = {};
      if (typeof o.name === "string") offer.name = o.name;
      if (isObj(o.offer_rules)) offer.offer_rules = o.offer_rules;
      if (isObj(o.ui_content)) offer.ui_content = o.ui_content;
      return { v: 1, type: "campaign:replace", offer };
    }
    default:
      return null;
  }
};

export const parsePreviewMessage = (data: unknown): PreviewToBuilder | null => {
  if (!isObj(data) || data.v !== PREVIEW_PROTOCOL_VERSION) return null;
  switch (data.type) {
    case "ready":
      return { v: 1, type: "ready" };
    case "section:clicked":
      return isId(data.id) ? { v: 1, type: "section:clicked", id: data.id } : null;
    case "section:hover":
      return data.id === null || isId(data.id) ? { v: 1, type: "section:hover", id: data.id as string | null } : null;
    case "navigate":
      return typeof data.path === "string" && data.path.startsWith("/") && !data.path.startsWith("//") ? { v: 1, type: "navigate", path: data.path } : null;
    case "region:clicked":
      return isRegion(data.region) ? { v: 1, type: "region:clicked", region: data.region } : null;
    case "region:hover":
      return data.region === null || isRegion(data.region) ? { v: 1, type: "region:hover", region: data.region as PreviewRegion | null } : null;
    case "field:edit":
      return isId(data.sectionId) && isFieldPath(data.path) && (data.locale === "en" || data.locale === "bn") && typeof data.value === "string" && data.value.length <= MAX_INLINE_TEXT
        ? { v: 1, type: "field:edit", sectionId: data.sectionId, path: data.path, locale: data.locale, value: data.value }
        : null;
    case "image:pick":
      return isId(data.sectionId) && isFieldPath(data.path) ? { v: 1, type: "image:pick", sectionId: data.sectionId, path: data.path } : null;
    case "fields:open":
      return isId(data.sectionId) && Array.isArray(data.paths) && data.paths.length > 0 && data.paths.length <= 8 && data.paths.every(isFieldPath)
        ? { v: 1, type: "fields:open", sectionId: data.sectionId, paths: data.paths as string[], ...(typeof data.label === "string" ? { label: data.label.slice(0, 80) } : {}) }
        : null;
    case "shortcut":
      return data.action === "undo" || data.action === "redo" || data.action === "save" ? { v: 1, type: "shortcut", action: data.action } : null;
    case "error":
      return typeof data.message === "string" ? { v: 1, type: "error", message: data.message.slice(0, 500) } : null;
    default:
      return null;
  }
};

/** Storefront path that shows a builder page key (pages needing a record, like product, use a sample). */
export const PREVIEW_PAGE_PATHS: Record<string, string> = {
  home: "/",
  shop: "/shop",
  categories: "/categories",
  cart: "/cart",
  checkout: "/checkout",
  contact: "/contact",
  signin: "/signin",
  signup: "/signup",
  account: "/account",
  wishlist: "/wishlist",
  compare: "/compare",
  coupon: "/coupon",
  blog: "/blog",
  "track-order": "/track-order",
  "privacy-policy": "/privacy-policy",
  "terms-of-service": "/terms-of-service",
  "shipping-policy": "/shipping-policy",
  "return-policy": "/return-policy",
  offers: "/offers",
  tools: "/tools",
  "pc-builder": "/pc-builder",
};

/** Storefront routes that show a record, and the builder page keys they read, most specific first. */
const RECORD_ROUTES: [RegExp, (slug: string) => string[]][] = [
  [/^\/product\/[^/]+$/, () => ["product"]],
  [/^\/category\/([^/]+)$/, (slug) => [`category-${slug}`, "category"]],
  [/^\/blog\/([^/]+)$/, (slug) => [`blog-${slug}`, "blog"]],
  [/^\/order-success\/[^/]+$/, () => ["order-success"]],
  [/^\/reset-password\/[^/]+$/, () => ["reset-password"]],
  [/^\/account\/orders(\/.*)?$/, () => ["orders", "account"]],
  [/^\/account\/(profile|addresses)$/, (sub) => [sub, "account"]],
];

/**
 * The builder page key for a storefront path the preview navigated to: the first of the route's page keys the
 * builder knows, or null when the path is not a builder page. The reverse of {@link PREVIEW_PAGE_PATHS}.
 */
export const pageKeyForPath = (path: string, known: readonly string[]): string | null => {
  const pathname = (path.split(/[?#]/)[0] ?? "/").replace(/(.)\/$/, "$1");
  const fixed = Object.entries(PREVIEW_PAGE_PATHS).find(([, p]) => p === pathname)?.[0];
  let candidates = fixed ? [fixed] : [pathname.slice(1)];
  for (const [pattern, keys] of RECORD_ROUTES) {
    const m = pattern.exec(pathname);
    if (m) {
      candidates = keys(decodeURIComponent(m[1] ?? ""));
      break;
    }
  }
  return candidates.find((key) => known.includes(key)) ?? null;
};

/**
 * The editor shortcut a key press stands for: Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y redo, Ctrl/Cmd+S
 * save. Shared by the editor and the preview so both follow the same keys.
 */
export const shortcutForKey = (e: { key: string; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): PreviewShortcut | null => {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return null;
  const key = e.key.toLowerCase();
  if (key === "z") return e.shiftKey ? "redo" : "undo";
  if (key === "y") return "redo";
  if (key === "s") return "save";
  return null;
};
